import type { SetCostOverrideRequest } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as costOverridesApi from "@/lib/api/flow-instances/cost-overrides";
import { usePublicSession } from "@/lib/context/public-session-context";
import { flowInstanceQueryKeys } from "./use-flow-instances";
import { goalSnapshotQueryKeys } from "./use-goal-snapshots";

// QL-20 — the advisor's fee overrides and the fee rows they change.
export const costOverrideQueryKeys = {
  list: (instanceId: string | null) => ["flowInstances", instanceId, "costOverrides"] as const,
  portfolioCosts: (instanceId: string | null) =>
    ["flowInstances", instanceId, "portfolioCosts"] as const,
  publicPortfolioCosts: (instanceId: string | null) =>
    ["publicFlow", "instance", instanceId, "portfolioCosts"] as const,
};

/** Per-portfolio reads an override changes: every portfolio of the instance carries it. */
const PORTFOLIO_COST_READS = new Set(["cost", "costBenefit", "productCosts"]);

/**
 * After a save or clear (mirrored by the AI's costOverrideInvalidateKeys): the
 * metadata, whose snapshotsRegenerating starts the wizard's rebuild watch, the
 * override list, the fee rows, product costs, the instance cost aggregate, and
 * each portfolio's cost, cost/benefit and product-cost reads.
 */
function invalidateAfterOverride(
  queryClient: ReturnType<typeof useQueryClient>,
  instanceId: string,
) {
  for (const queryKey of [
    flowInstanceQueryKeys.metadata(instanceId),
    costOverrideQueryKeys.list(instanceId),
    costOverrideQueryKeys.portfolioCosts(instanceId),
    flowInstanceQueryKeys.productCosts(instanceId),
    goalSnapshotQueryKeys.instanceCost(instanceId),
  ]) {
    queryClient.invalidateQueries({ queryKey });
  }
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === "flowInstances" &&
      queryKey[1] === "portfolios" &&
      queryKey[3] === "snapshot" &&
      PORTFOLIO_COST_READS.has(String(queryKey[4])),
  });
}

/**
 * The collection's fee rows as this instance sees them (overrides applied, the
 * catalogue figures beside them). Public: read through the client session.
 */
export function useInstancePortfolioCosts(instanceId: string | null) {
  const session = usePublicSession();
  const { data, isLoading } = useQuery({
    queryKey: session.isPublic
      ? costOverrideQueryKeys.publicPortfolioCosts(instanceId)
      : costOverrideQueryKeys.portfolioCosts(instanceId),
    queryFn: () =>
      session.isPublic
        ? costOverridesApi.getPublicInstancePortfolioCosts(session.sessionToken)
        : costOverridesApi.getInstancePortfolioCosts(instanceId!),
    // The public read is scoped by the session, so it needs no instance id.
    enabled: session.isPublic || !!instanceId,
  });
  return { portfolioCosts: data?.portfolioCosts ?? null, isLoading };
}

/** The overrides on the instance, with the ids a clear needs. */
export function useCostOverrides(instanceId: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: costOverrideQueryKeys.list(instanceId),
    queryFn: () => costOverridesApi.listCostOverrides(instanceId!),
    enabled: !!instanceId,
  });
  return { overrides: data?.overrides ?? [], isLoading };
}

export function useSetCostOverride(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetCostOverrideRequest) =>
      costOverridesApi.setCostOverride(instanceId, payload),
    onSuccess: () => invalidateAfterOverride(queryClient, instanceId),
  });
}

export function useClearCostOverride(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (overrideId: string) => costOverridesApi.clearCostOverride(instanceId, overrideId),
    onSuccess: () => invalidateAfterOverride(queryClient, instanceId),
  });
}
